import React from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';

import { catalogBibShape, listShape, recommendationShape } from '@bibliocommons/bc-prop-types';
import Divider from 'app/components/bibs/shared/Divider';
import { bindAll } from '@bibliocommons/utils-react';

import HeaderBar from 'app/components/bibs/shared/HeaderBar';
import { FORMAT_ID } from 'app/constants/displayBib';
import cardShape from 'app/shapes/cardShape';
import { authorSuggestionShape, onlineResourceShape, relatedAuthorShape, tagShape } from 'app/shapes/bibPage';
import { About, ResearchAbout } from './About';

import Community from './Community';
import Discover from './Discover';
import EventCards from './EventCards';
import Explore from './Explore';
import Movies from './Movies';
import Opinion from './Opinion';
import Transact from './Transact';

import './BibPage.scss';

export class BibPage extends React.PureComponent {
  constructor(props) {
    super(props);

    this.trackAuthorClick = this.makeTrackLinkClickHandler('bibliographic_details_creators');
    this.trackRelatedAuthorClick = this.makeTrackLinkClickHandler('bibliographic_explore_creators_related');
    this.trackSimilarAuthorClick = this.makeTrackLinkClickHandler('bibliographic_explore_creators_similar');

    bindAll(this);
  }

  makeTrackLinkClickHandler = metadataCategoryType => {
    return function trackLinkClick(linkText) {
      const { catalogBib, trackCatalogExplore } = this.props;

      trackCatalogExplore({
        bibId: catalogBib.get('metadataId'),
        metadata: {
          metadata_category_type: metadataCategoryType,
          metadata_value: linkText
        },
        ui: {
          ui_component_label: linkText,
          ui_component_type: 'link-text'
        }
      });
    };
  };

  trackFieldItemClick(category, fieldItem, fieldValue) {
    const { catalogBib, trackCatalogExplore } = this.props;

    const fieldName = fieldItem.get('fieldName');
    const values = fieldValue.getIn(['primary', 'values'], []) ?? [];

    const linkText = values.join(', ');

    trackCatalogExplore({
      bibId: catalogBib.get('metadataId'),
      metadata: {
        metadata_category_type: `bibliographic_${category.toLowerCase()}_${fieldName.toLowerCase()}`,
        metadata_value: linkText
      },
      ui: {
        ui_component_label: linkText,
        ui_component_type: 'link-text'
      }
    });
  }

  render() {
    const {
      catalogBib,
      eventCards,
      promoCards,
      staffLists,
      showResearchAbout,
      recommendations,
      relatedTitles,
      tags,
      authorSuggestions,
      relatedAuthors,
      onlineResources,
      handleRelatedTitleClick,
      trackEventCardImpression,
      trackEventCardLocationClick,
      trackEventCardTitleClick
    } = this.props;

    const divider = (
      <div className="container">
        <Divider />
      </div>
    );

    const format = catalogBib.getIn(['brief', 'format']);
    const researchFormats = [
      FORMAT_ID.ARCHIVAL_CONTAINER,
      FORMAT_ID.ARCHIVAL_OBJECT_DIGITAL,
      FORMAT_ID.ARCHIVAL_OBJECT,
      FORMAT_ID.MANUSCRIPT,
      FORMAT_ID.MICROFORM,
      FORMAT_ID.RARE_BK
    ];
    const isResearchFormat = researchFormats.includes(format);

    return (
      <div className="cp-bib-page" data-key="bib-page">
        <div className="container">
          <HeaderBar />
        </div>
        <Transact catalogBib={catalogBib} trackAuthorClick={this.trackAuthorClick} />
        {eventCards.size === 0 && divider}
        <Movies catalogBib={catalogBib} />
        <EventCards
          eventCards={eventCards}
          trackEventCardImpression={trackEventCardImpression}
          trackEventCardLocationClick={trackEventCardLocationClick}
          trackEventCardTitleClick={trackEventCardTitleClick}
        />
        {showResearchAbout && isResearchFormat ? (
          <ResearchAbout trackFieldItemClick={this.trackFieldItemClick} />
        ) : (
          <About trackFieldItemClick={this.trackFieldItemClick} />
        )}
        {divider}
        <Opinion catalogBib={catalogBib} />
        <Discover
          catalogBib={catalogBib}
          promoCards={promoCards}
          staffLists={staffLists}
          recommendations={recommendations}
        />
        <Explore
          authorSuggestions={authorSuggestions}
          catalogBib={catalogBib}
          handleRelatedTitleClick={handleRelatedTitleClick}
          onlineResources={onlineResources}
          relatedAuthors={relatedAuthors}
          relatedTitles={relatedTitles}
          tags={tags}
          trackRelatedAuthorClick={this.trackRelatedAuthorClick}
          trackSimilarAuthorClick={this.trackSimilarAuthorClick}
        />
        <Community catalogBib={catalogBib} />
      </div>
    );
  }
}

BibPage.propTypes = {
  catalogBib: catalogBibShape.isRequired,

  // Props below are used to conditionally render section titles and dividers
  authorSuggestions: ImmutablePropTypes.listOf(authorSuggestionShape).isRequired,
  eventCards: ImmutablePropTypes.listOf(cardShape).isRequired,
  handleRelatedTitleClick: PropTypes.func,
  onlineResources: ImmutablePropTypes.listOf(onlineResourceShape).isRequired,
  promoCards: ImmutablePropTypes.listOf(cardShape).isRequired,
  recommendations: ImmutablePropTypes.listOf(recommendationShape).isRequired,
  relatedAuthors: ImmutablePropTypes.listOf(relatedAuthorShape).isRequired,
  relatedTitles: ImmutablePropTypes.listOf(catalogBibShape).isRequired,
  showResearchAbout: PropTypes.bool.isRequired,
  staffLists: ImmutablePropTypes.listOf(listShape).isRequired,
  tags: ImmutablePropTypes.listOf(tagShape).isRequired,
  trackCatalogExplore: PropTypes.func.isRequired,
  trackEventCardImpression: PropTypes.func.isRequired,
  trackEventCardLocationClick: PropTypes.func.isRequired,
  trackEventCardTitleClick: PropTypes.func.isRequired
};

export default BibPage;
